<template>
  <div v-if="isLoading" id="loading_box">
    <div class="splash-logo">
      <object
        id="loadingLogoAnim"
        type="image/svg+xml"
        :data="dynamicImageUrlTransPath('/assets/pages/common/Logo_Collar.svg')"
        class="icon"
        :class="{ '-start': isIconAnimating }"
      ></object>
    </div>
  </div>
</template>
<script setup lang="ts">
const firstLoadingSessionStorageKeyName = 'firstLoading'

const enum loadingState {
  NotLoading,
  IconLoadingAnimation,
  IconLoadingAnimationComplete,
  LoadingComplete,
}

const currentLoadingState = ref<loadingState>(loadingState.NotLoading)
const nuxtApp = useNuxtApp()
const isLoading = ref(true)
const loadingComplete = ref(false)

const isIconAnimating = computed(
  () => currentLoadingState.value !== loadingState.NotLoading
)

onMounted(() => {
  checkNuxtPagePath()
})

watch(currentLoadingState, () => {
  switch (currentLoadingState.value) {
    case loadingState.NotLoading: {
      break
    }
    case loadingState.IconLoadingAnimation: {
      setTimeout(() => {
        currentLoadingState.value = loadingState.IconLoadingAnimationComplete
      }, 2000)
      break
    }
    case loadingState.IconLoadingAnimationComplete: {
      if (loadingComplete.value) {
        currentLoadingState.value = loadingState.LoadingComplete
      }
      break
    }
    case loadingState.LoadingComplete: {
      const startBoxBefore = document.getElementById(
        'loading_box'
      ) as HTMLDivElement
      if (!startBoxBefore) return
      startBoxBefore.classList.add('animation-running')
      sessionStorage.setItem(firstLoadingSessionStorageKeyName, 'true')
      setTimeout(() => {
        isLoading.value = false
      }, 3000)
      break
    }
  }
})

const startIconAnimation = () => {
  if (currentLoadingState.value !== loadingState.NotLoading) return
  currentLoadingState.value = loadingState.IconLoadingAnimation
}

const checkNuxtPagePath = () => {
  const firstLoading = sessionStorage.getItem(firstLoadingSessionStorageKeyName)
  if (firstLoading) {
    isLoading.value = false
    return
  }

  const object = document.getElementById('loadingLogoAnim') as HTMLObjectElement
  if (!object) return

  if (object.contentDocument?.readyState === 'complete') {
    startIconAnimation()
    return
  }
  object.addEventListener('load', startIconAnimation, { once: true })
}

nuxtApp.hook('page:finish', () => {
  loadingComplete.value = true
  if (currentLoadingState.value === loadingState.IconLoadingAnimationComplete) {
    currentLoadingState.value = loadingState.LoadingComplete
  }
})
</script>
<style lang="scss" scoped>
@use '@/assets/styles/variables' as v;

@keyframes fadeIn {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes logoFade {
  0% {
    opacity: 0;
    -webkit-filter: blur(50px);
    filter: blur(50px);
  }
  45% {
    opacity: 1;
    -webkit-filter: blur(0);
    filter: blur(0);
  }
  85% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

#loading_box {
  margin: auto;
  width: 100%;
  height: 100vh;
  background-color: v.$common-white-color;
  position: fixed;
  z-index: 1000;
  inset: 0;
}

.splash-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  .icon {
    width: 160px;
    opacity: 0;
  }

  .icon.-start {
    animation: logoFade 3.5s ease-in-out 0.2s forwards;
  }
}

.animation-running {
  animation: fadeIn 0.8s ease 1.5s 1 forwards;
  animation-play-state: running;
}
</style>
